import { ENEMY } from '../config/config';
import type { World } from '../game/world';
import type { Renderer } from '../gfx/renderer';
import type { Camera } from '../world/camera';
import type { Entity } from './entity';

/**
 * Entity lifecycle: activation near the camera, updates, culling.
 *
 * Enemies stay dormant (frozen at their spawn position) until the camera is close, so a
 * grub never walks off a ledge before the player has seen it, and off-screen entities
 * cost nothing.
 */
export class EntityManager {
  list: Entity[] = [];
  /** Number of entities updated on the last step (debug readout). */
  activeCount = 0;

  add(e: Entity): void {
    this.list.push(e);
  }

  clear(): void {
    this.list.length = 0;
  }

  update(dt: number, world: World, camera: Camera): void {
    let active = 0;
    for (const e of this.list) {
      if (!e.alive) continue;
      if (!e.awake) {
        const w = Math.max(e.w, 16);
        const h = Math.max(e.h, 16);
        if (camera.nearView(e.x, e.y, w, h, ENEMY.activateMargin)) e.awake = true;
        else continue;
      }
      e.update(dt, world);
      active++;
    }
    this.activeCount = active;

    // cull: dead, fallen out of the world, or left far behind the camera
    const cutoff = camera.x - ENEMY.despawnMargin;
    for (let i = this.list.length - 1; i >= 0; i--) {
      const e = this.list[i];
      const gone =
        !e.alive ||
        e.y > world.map.pixelH + 96 ||
        (!e.alwaysAwake && e.awake && e.x + e.w < cutoff);
      if (gone) this.list.splice(i, 1);
    }
  }

  draw(r: Renderer, world: World, camera: Camera): void {
    const visible = this.list.filter(
      (e) => e.alive && e.awake && camera.nearView(e.x, e.y, Math.max(e.w, 16), Math.max(e.h, 24), 24),
    );
    visible.sort((a, b) => a.z - b.z);
    for (const e of visible) e.draw(r, world);
  }
}
